/**
 * ControlHub - Collapsed launcher with nudge behavior (FLO-585).
 * Shown when the unified panel is minimized.
 *
 * FLO-585: the launcher is now the CUSTOMER's brand, not ours. It renders the
 * same branded face as the website agent's "Ask {site}" pill — site logo →
 * favicon → letter-mark on the accent ({@link LauncherMark}) — and the Floe
 * SVG + dark gradient are gone. It is also a real button element (native focus + Enter/Space),
 * honours `prefers-reduced-motion`, and can be moved out of the contested
 * bottom-right corner by configuration.
 *
 * Clicking the launcher opens the unified panel.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Nudge } from './Nudge';
import { LauncherMark } from './LauncherMark';
import { AgentState } from '../types/agent';
import { logger } from '../sdk/logger';

interface ControlHubProps {
  connected: boolean;
  agentState: AgentState;
  /**
   * The user wants the agent. FLO-51: this is now the ONLY signal the orb
   * emits — the caller (OnboardingView) both opens the panel and starts the
   * session from it, synchronously, so the click stays a transient user
   * activation and the iOS mic prompt can appear (FLO-109). It replaces the
   * old onExpand + onToggleConnection pair, which split one intent across two
   * callbacks and let the orb open a panel without a session behind it.
   */
  onExpand?: () => void;
  /** Only input to the "Welcome back" copy (FLO-534). */
  isReturningUser?: boolean;
  // Nudge props
  nudgeText?: string;
  showNudge?: boolean;
  onNudgeDismiss?: () => void;
  nudgeAutoHideDelay?: number;
  nudgeAutoShow?: boolean;
  // ─── FLO-585: customer brand + position ────────────────────────────────────
  siteName?: string;
  siteLogoUrl?: string;
  siteFaviconUrl?: string;
  /** Resolved brand accent (#RRGGBB). */
  accent?: string;
  /** Which corner the launcher anchors to. Default 'bottom-right'. */
  position?: 'bottom-right' | 'bottom-left';
  /** Inset from the viewport edge in px. Default 24. */
  inset?: number;
}

// Nudge persistence storage keys
const NUDGE_STORAGE_KEY = 'floe_nudge_state';
const NUDGE_SOUND_PLAYED_KEY = 'floe_nudge_sound_played';

// Nudge UX configuration
const NUDGE_CONFIG = {
  initialDelay: 5000,
  autoHideDuration: 10000,
  maxImpressions: 3,
  cooldownDays: 7,
};

// FLO-585: canonical Floe coral, so an un-branded site still gets a deliberate
// accent (matches the website agent's DEFAULT_ACCENT) rather than our old dark orb.
const DEFAULT_ACCENT = '#FF5C35';

/** The branded face diameter, and the launcher's rendered height for the nudge. */
const MARK_SIZE = 32;
const LAUNCHER_HEIGHT_PX = 52;
/** Trailing glyph edge, in px. Applied inline AND in CSS — see LAUNCHER_GLYPH_PX use. */
const LAUNCHER_GLYPH_PX = 18;

/** One font stack for the closed-state surface (matches the website agent). */
const LAUNCHER_FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

/**
 * The visible label while a session is live but minimized. The point of FLO-585's
 * live-state fix: a collapsed session is still connected and still billing, so the
 * pill says so instead of reading like an idle "ask me" button.
 */
function liveStateLabel(state: AgentState): string {
  switch (state) {
    case 'connecting': return 'Connecting…';
    case 'listening': return 'Listening…';
    case 'thinking': return 'Thinking…';
    case 'speaking': return 'Speaking…';
    case 'guiding': return 'Guiding…';
    case 'paused': return 'Paused';
    default: return 'Live'; // 'idle' + 'minimized' → "still running"
  }
}

export const ControlHub: React.FC<ControlHubProps> = ({
  connected,
  agentState,
  onExpand,
  isReturningUser = false,
  // Nudge props
  nudgeText = 'Hi 👋 How can I help you today?',
  showNudge: externalShowNudge,
  onNudgeDismiss,
  nudgeAutoHideDelay,
  nudgeAutoShow = true,
  // FLO-585: brand + position
  siteName,
  siteLogoUrl,
  siteFaviconUrl,
  accent: accentProp = DEFAULT_ACCENT,
  position = 'bottom-right',
  inset,
}) => {
  // FLO-51: the launcher is the resting state for EVERY user — the page-load
  // welcome modal is gone, so nobody starts with the panel open. That made the
  // old three-reason `shouldStartMinimized` (returning / skipped / support mode)
  // vacuous, and the two props feeding it were removed rather than left here
  // being read by nothing.
  //
  // What the launcher still needs to know is what a click MEANS: start a session
  // (nothing running) or re-open the panel of one that is. Support mode keeps
  // its distinct behaviour through `nudgeAutoShow`, which the SDK resolves to
  // false for it (resolveNudgeAutoShow) — an on-demand agent must not tap the
  // user on the shoulder. `isReturningUser` stays the ONLY input to the
  // "Welcome back" copy: a first-time visitor on a support-mode site is not a
  // returning user (FLO-534).
  const willStartSession = !connected;

  const [internalShowNudge, setInternalShowNudge] = useState(false);
  const [nudgeDismissed, setNudgeDismissed] = useState(false);

  // ============================================
  // NUDGE UX: Non-intrusive behavior helpers
  // ============================================

  const shouldShowNudgeBasedOnHistory = useCallback(() => {
    try {
      const stored = localStorage.getItem(NUDGE_STORAGE_KEY);
      if (!stored) return true;
      const state = JSON.parse(stored);
      if (state.hasEngaged) return false;
      if (state.impressions >= NUDGE_CONFIG.maxImpressions) return false;
      if (state.lastDismissed) {
        const daysSinceDismiss = (Date.now() - state.lastDismissed) / (1000 * 60 * 60 * 24);
        if (daysSinceDismiss < NUDGE_CONFIG.cooldownDays) return false;
      }
      return true;
    } catch {
      return true;
    }
  }, []);

  const recordNudgeImpression = useCallback(() => {
    try {
      const stored = localStorage.getItem(NUDGE_STORAGE_KEY);
      const state = stored ? JSON.parse(stored) : { impressions: 0 };
      state.impressions = (state.impressions || 0) + 1;
      state.lastShown = Date.now();
      localStorage.setItem(NUDGE_STORAGE_KEY, JSON.stringify(state));
    } catch { /* localStorage not available */ }
  }, []);

  const recordNudgeDismiss = useCallback(() => {
    try {
      const stored = localStorage.getItem(NUDGE_STORAGE_KEY);
      const state = stored ? JSON.parse(stored) : { impressions: 0 };
      state.lastDismissed = Date.now();
      localStorage.setItem(NUDGE_STORAGE_KEY, JSON.stringify(state));
    } catch { /* localStorage not available */ }
  }, []);

  const recordEngagement = useCallback(() => {
    try {
      const stored = localStorage.getItem(NUDGE_STORAGE_KEY);
      const state = stored ? JSON.parse(stored) : { impressions: 0 };
      state.hasEngaged = true;
      localStorage.setItem(NUDGE_STORAGE_KEY, JSON.stringify(state));
    } catch { /* localStorage not available */ }
  }, []);

  const playNudgeSound = useCallback(() => {
    try {
      if (sessionStorage.getItem(NUDGE_SOUND_PLAYED_KEY)) return;
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioContextClass) return;
      const audioContext = new AudioContextClass();
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }
      const now = audioContext.currentTime;

      const osc1 = audioContext.createOscillator();
      const gain1 = audioContext.createGain();
      osc1.connect(gain1);
      gain1.connect(audioContext.destination);
      osc1.frequency.setValueAtTime(659, now);
      osc1.type = 'sine';
      gain1.gain.setValueAtTime(0.25, now);
      gain1.gain.exponentialRampToValueAtTime(0.01, now + 0.3);
      osc1.start(now);
      osc1.stop(now + 0.3);

      const osc2 = audioContext.createOscillator();
      const gain2 = audioContext.createGain();
      osc2.connect(gain2);
      gain2.connect(audioContext.destination);
      osc2.frequency.setValueAtTime(784, now + 0.12);
      osc2.type = 'sine';
      gain2.gain.setValueAtTime(0, now);
      gain2.gain.setValueAtTime(0.2, now + 0.12);
      gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.5);
      osc2.start(now + 0.12);
      osc2.stop(now + 0.5);

      const osc3 = audioContext.createOscillator();
      const gain3 = audioContext.createGain();
      osc3.connect(gain3);
      gain3.connect(audioContext.destination);
      osc3.frequency.setValueAtTime(1318, now);
      osc3.type = 'sine';
      gain3.gain.setValueAtTime(0.08, now);
      gain3.gain.exponentialRampToValueAtTime(0.001, now + 0.2);
      osc3.start(now);
      osc3.stop(now + 0.2);

      osc2.onended = () => { audioContext.close(); };
      logger.log('[Floe] Nudge sound played');
      sessionStorage.setItem(NUDGE_SOUND_PLAYED_KEY, 'true');
    } catch {
      // Audio not supported or blocked
    }
  }, []);

  // Nudge visibility
  const showNudge = externalShowNudge !== undefined
    ? externalShowNudge
    : (internalShowNudge && !nudgeDismissed && !connected);

  // Auto-show nudge
  useEffect(() => {
    if (externalShowNudge !== undefined) return;
    if (!nudgeAutoShow) return;
    if (willStartSession && !nudgeDismissed) {
      if (!shouldShowNudgeBasedOnHistory()) return;
      const showTimer = setTimeout(() => {
        setInternalShowNudge(true);
        recordNudgeImpression();
        playNudgeSound();
      }, NUDGE_CONFIG.initialDelay);

      const autoHideDuration =
        nudgeAutoHideDelay === undefined ? NUDGE_CONFIG.autoHideDuration : nudgeAutoHideDelay;

      let hideTimer: ReturnType<typeof setTimeout> | undefined;
      if (autoHideDuration > 0) {
        hideTimer = setTimeout(() => {
          setInternalShowNudge(false);
          recordNudgeDismiss();
        }, NUDGE_CONFIG.initialDelay + autoHideDuration);
      }

      return () => {
        clearTimeout(showTimer);
        if (hideTimer) clearTimeout(hideTimer);
      };
    }
  }, [willStartSession, nudgeDismissed, shouldShowNudgeBasedOnHistory, recordNudgeImpression, recordNudgeDismiss, playNudgeSound, externalShowNudge, nudgeAutoShow, nudgeAutoHideDelay]);

  const handleNudgeDismiss = useCallback(() => {
    setInternalShowNudge(false);
    setNudgeDismissed(true);
    recordNudgeDismiss();
    onNudgeDismiss?.();
  }, [recordNudgeDismiss, onNudgeDismiss]);

  const handleNudgeClick = useCallback(() => {
    setInternalShowNudge(false);
    recordEngagement();
    // onExpand IS the start signal now: it opens the panel AND boots the
    // session in one synchronous step, so the click stays a transient user
    // activation (FLO-51 / FLO-109).
    onExpand?.();
  }, [recordEngagement, onExpand]);

  const handleOrbClick = useCallback(() => {
    recordEngagement();
    // onExpand IS the start signal now: it opens the panel AND boots the
    // session in one synchronous step, so the click stays a transient user
    // activation (FLO-51 / FLO-109).
    onExpand?.();
  }, [recordEngagement, onExpand]);

  const getStateColor = () => {
    switch (agentState) {
      case 'connecting': return '#fbbf24';
      case 'listening': return '#60a5fa';
      case 'thinking': return '#fbbf24';
      case 'speaking': return '#34d399';
      case 'guiding': return '#22d3ee';
      case 'paused': return '#f59e0b';
      case 'minimized': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const accent = accentProp.trim() || DEFAULT_ACCENT;
  // The agent speaks AS the site, so the site name is the agent's name — the
  // same fallback the website agent's launcher and chat header use.
  const agentName = siteName?.trim() || 'Floe';
  const letter = (siteName?.trim()?.[0] || 'F').toUpperCase();
  const stateColor = getStateColor();
  const corner = position === 'bottom-left' ? 'bottom-left' : 'bottom-right';
  // Non-numeric / non-finite → the 24px default; a negative value would park the
  // launcher off-screen (bottom/right: -Npx), so clamp valid insets to ≥0.
  const insetPx = typeof inset === 'number' && Number.isFinite(inset)
    ? Math.max(0, Math.round(inset))
    : 24;

  // WCAG 2.5.3: the accessible name must contain the visible label text so
  // speech-input users can say "click Ask Acme" and have it match.
  const visibleLabel = willStartSession ? `Ask ${agentName}` : liveStateLabel(agentState);
  const ariaLabel = willStartSession
    ? (isReturningUser ? `Welcome back — Ask ${agentName}` : `Ask ${agentName}`)
    : `${visibleLabel} — Expand ${agentName}`;

  // Approximate width so the nudge overlap clears the entire pill, not just the
  // old 52px orb. The visible label drives the pill width; the rest is fixed
  // (mark + gaps + padding + border + icon). Char width ≈ 9px at 15px font.
  const launcherWidth = MARK_SIZE + 10 + 10 + 28 + visibleLabel.length * 9 + 18;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: `${insetPx}px`,
        ...(corner === 'bottom-left'
          ? { left: `${insetPx}px`, justifyContent: 'flex-start' }
          : { right: `${insetPx}px`, justifyContent: 'flex-end' }),
        display: 'flex',
        alignItems: 'center',
        zIndex: 2147483645,
        pointerEvents: 'none',
      }}
    >
      {/* Nudge - positioned absolutely, extends under the launcher */}
      <Nudge
        text={nudgeText}
        isVisible={showNudge}
        onClick={handleNudgeClick}
        onAutoHide={handleNudgeDismiss}
        orbSize={LAUNCHER_HEIGHT_PX}
        overlap={corner === 'bottom-left' ? 'left' : 'right'}
        launcherWidth={launcherWidth}
      />

      {/* The launcher — a real button element now (FLO-585): native focus ring and
          Enter/Space, no hand-rolled keydown. */}
      <button
        type="button"
        aria-label={ariaLabel}
        onClick={handleOrbClick}
        className="floe-ob-launcher"
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          boxSizing: 'border-box',
          // A long site name can only run off the far edge — cap and ellipsize.
          maxWidth: `min(240px, calc(100vw - ${2 * insetPx}px))`,
          // The overlay renders in the LIGHT DOM, so host CSS reaches in. Pin the
          // height rather than letting content size it: greytHR bundles nvd3's bare
          // `svg{width:100%;height:100%}`, which beat the glyph's presentation
          // attributes and inflated this pill into a ~240px circle. With a definite
          // height (and a definite glyph below) no host rule can change the geometry.
          // LAUNCHER_HEIGHT_PX was already the documented height and is what the
          // Nudge is positioned against — it was just never applied here.
          height: LAUNCHER_HEIGHT_PX,
          padding: '9px 16px 9px 10px',
          background: '#FFFFFF',
          border: '1px solid rgba(0,0,0,0.06)',
          borderRadius: 999,
          cursor: 'pointer',
          pointerEvents: 'all',
          flexShrink: 0,
          zIndex: 1,
          fontFamily: LAUNCHER_FONT,
          ['--floe-ob-accent' as any]: accent,
        }}
      >
        {/* Branded face + live ring. Idle → logo/letter on the accent. Live →
            a state-coloured ring + pulse so a collapsed (still-billing) session
            reads as "running", not "idle". */}
        <span style={{ position: 'relative', width: MARK_SIZE, height: MARK_SIZE, flexShrink: 0 }}>
          <LauncherMark
            siteLogoUrl={siteLogoUrl}
            siteFaviconUrl={siteFaviconUrl}
            letter={letter}
            accent={accent}
            size={MARK_SIZE}
          />
          {!willStartSession && (
            <span
              aria-hidden="true"
              style={{
                position: 'absolute',
                inset: -3,
                borderRadius: '50%',
                border: `2px solid ${stateColor}`,
                pointerEvents: 'none',
              }}
            />
          )}
          {!willStartSession && (
            <span
              aria-hidden="true"
              className="floe-ob-pulse"
              style={{
                position: 'absolute',
                inset: -3,
                borderRadius: '50%',
                border: `2px solid ${stateColor}`,
                pointerEvents: 'none',
                opacity: 0.5,
              }}
            />
          )}
        </span>

        <span
          style={{
            fontSize: 15,
            fontWeight: 600,
            color: '#1A1816',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minWidth: 0,
          }}
        >
          {visibleLabel}
        </span>

        {willStartSession ? (
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke={accent}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            style={{ width: LAUNCHER_GLYPH_PX, height: LAUNCHER_GLYPH_PX, display: 'block', flexShrink: 0 }}
          >
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        ) : (
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke={stateColor}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            style={{ width: LAUNCHER_GLYPH_PX, height: LAUNCHER_GLYPH_PX, display: 'block', flexShrink: 0 }}
          >
            <polyline points="18 15 12 9 6 15" />
          </svg>
        )}
      </button>

      <style>{`
        @keyframes floe-ob-launcher-in {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes floe-ob-pulse {
          0% { transform: scale(1); opacity: 0.5; }
          100% { transform: scale(1.6); opacity: 0; }
        }
        .floe-ob-launcher {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          box-shadow: 0 12px 40px -8px rgba(0,0,0,0.28), 0 2px 8px rgba(0,0,0,0.10);
          animation: floe-ob-launcher-in 0.4s cubic-bezier(0.16,1,0.3,1);
        }
        /* Second layer of the light-DOM defence. Inline styles already beat a
           host author-NORMAL rule; this covers host !important ones.

           Specificity alone cannot do it: '.floe-ob-launcher > svg' is (0,1,1),
           so a host '.app button svg { width:100% !important }' at (0,1,2) still
           wins, and out-specifying is an arms race we lose to the first #id.
           Instead use the cascade-layer rule that makes specificity moot — for
           IMPORTANT declarations the cascade sorts by layer BEFORE specificity
           and reverses layer order, and unlayered styles are the implicit FINAL
           layer. So an important declaration inside any explicit @layer beats an
           unlayered important one at any specificity, and host CSS is
           overwhelmingly unlayered.

           Only the !important rules go inside the layer. Layering NORMAL
           declarations has the opposite effect — unlayered host styles would
           then beat them — which is why the hover/animation rules above stay
           out. Browsers without @layer drop this block and fall back to the
           inline styles, i.e. today's protection against non-important rules. */
        @layer floe-ob-guard {
          .floe-ob-launcher > svg {
            width: ${LAUNCHER_GLYPH_PX}px !important;
            height: ${LAUNCHER_GLYPH_PX}px !important;
            display: block !important;
            flex-shrink: 0 !important;
          }
          .floe-ob-launcher { height: ${LAUNCHER_HEIGHT_PX}px !important; }
        }
        .floe-ob-launcher:hover {
          transform: translateY(-2px);
          box-shadow: 0 16px 44px -8px rgba(0,0,0,0.32), 0 3px 10px rgba(0,0,0,0.12);
        }
        .floe-ob-launcher:focus-visible {
          outline: 2px solid var(--floe-ob-accent, #FF5C35);
          outline-offset: 2px;
        }
        .floe-ob-pulse { animation: floe-ob-pulse 2.5s ease-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .floe-ob-launcher { animation: none !important; transition: none !important; }
          .floe-ob-launcher:hover { transform: none; }
          .floe-ob-pulse { animation: none !important; opacity: 0 !important; }
        }
      `}</style>
    </div>
  );
};
